import { IApiError, BackendSource } from '../apiTypes';

/**
 * Error parser specifically for Puppeteer/Node.js backend API errors
 */
export class PuppeteerErrorHandler {
  /**
   * Extracts correlation ID from multiple sources
   */
  private static getCorrelationId(error: any, responseData?: any): string | undefined {
    // First try to get from response body
    if (responseData?.correlationId) {
      return responseData.correlationId;
    }
    if (responseData?.CorrelationId) {
      return responseData.CorrelationId;
    }
    // Then try response headers
    if (error.response?.headers?.['x-correlation-id']) {
      return error.response.headers['x-correlation-id'];
    }
    // Finally try request headers
    if (error.config?.headers?.['x-correlation-id']) {
      return error.config.headers['x-correlation-id'];
    }
    return undefined;
  }

  /**
   * Parses a blob error response asynchronously
   */
  static async parseBlobError(error: any, baseUrl: string = ''): Promise<IApiError> {
    const requestUrl = error.config?.url || '';

    if (error.response?.data instanceof Blob) {
      try {
        const contentType = error.response?.headers?.['content-type'] || 
                           error.response?.headers?.['Content-Type'] || '';
        const isJsonContentType = contentType.includes('application/json') || 
                                  contentType.includes('text/json');
        
        const text = await error.response.data.text();
        const cleanedText = text.trim().replace(/^\uFEFF/, '');
        const looksLikeJson = isJsonContentType || 
                             cleanedText.startsWith('{') || 
                             cleanedText.startsWith('[');
        
        if (looksLikeJson) {
          try {
            const parsedData = JSON.parse(cleanedText);
            
            // Extract fields (handle both camelCase and PascalCase)
            const message = parsedData.message || 
                           parsedData.Message || 
                           parsedData.error || 
                           parsedData.Error || 
                           'Ett fel uppstod vid filhantering';
            
            const statusCode = error.response.status || 
                              parsedData.statusCode || 
                              parsedData.StatusCode || 
                              parsedData.status || 
                              parsedData.Status || 
                              500;
            
            return {
              message: message,
              stack: parsedData.stack || parsedData.Stack || parsedData.stackTrace || parsedData.StackTrace || '',
              url: parsedData.url || parsedData.Url || requestUrl,
              statusCode: statusCode,
              timestamp: parsedData.timestamp || parsedData.Timestamp || new Date().toISOString(),
              correlationId: parsedData.correlationId || parsedData.CorrelationId || this.getCorrelationId(error, parsedData),
              backendSource: 'puppeteer' as BackendSource
            };
          } catch (parseError: any) {
            console.error('Failed to parse JSON from blob error response:', {
              parseError: parseError?.message || parseError,
              textLength: cleanedText.length,
              textPreview: cleanedText.substring(0, 200),
              contentType: error.response?.headers?.['content-type'],
              fullText: cleanedText
            });
            
            // Try to extract with regex
            const messageMatch = cleanedText.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const statusMatch = cleanedText.match(/"statusCode"\s*:\s*(\d+)/);
            const correlationMatch = cleanedText.match(/"correlationId"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const urlMatch = cleanedText.match(/"url"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const timestampMatch = cleanedText.match(/"timestamp"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const stackMatch = cleanedText.match(/"stack"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            
            if (messageMatch || statusMatch) {
              return {
                message: messageMatch?.[1]?.replace(/\\"/g, '"') || cleanedText.substring(0, 200) || 'Ett fel uppstod vid filhantering',
                stack: stackMatch?.[1]?.replace(/\\"/g, '"') || '',
                url: urlMatch?.[1]?.replace(/\\"/g, '"') || requestUrl,
                statusCode: statusMatch ? parseInt(statusMatch[1], 10) : (error.response.status || 500),
                timestamp: timestampMatch?.[1]?.replace(/\\"/g, '"') || new Date().toISOString(),
                correlationId: correlationMatch?.[1]?.replace(/\\"/g, '"') || this.getCorrelationId(error),
                backendSource: 'puppeteer' as BackendSource
              };
            }
            
            return {
              message: cleanedText.substring(0, 500) || 'Ett fel uppstod vid filhantering',
              stack: '',
              url: requestUrl,
              statusCode: error.response.status || 500,
              timestamp: new Date().toISOString(),
              correlationId: this.getCorrelationId(error),
              backendSource: 'puppeteer' as BackendSource
            };
          }
        } else {
          return {
            message: cleanedText || 'Ett fel uppstod vid filhantering',
            stack: '',
            url: requestUrl,
            statusCode: error.response.status || 500,
            timestamp: new Date().toISOString(),
            correlationId: this.getCorrelationId(error),
            backendSource: 'puppeteer' as BackendSource
          };
        }
      } catch (blobError) {
        console.error('Error reading blob:', blobError);
        return {
          message: 'Ett fel uppstod vid filhantering',
          stack: '',
          url: requestUrl,
          statusCode: error.response.status || 500,
          timestamp: new Date().toISOString(),
          correlationId: this.getCorrelationId(error),
          backendSource: 'puppeteer' as BackendSource
        };
      }
    }

    // Fallback to regular parsing if not a blob
    return this.parseError(error, baseUrl);
  }

  /**
   * Parses a Puppeteer/Node.js backend error response
   */
  static async parseError(error: any, baseUrl: string = ''): Promise<IApiError> {
    const requestUrl = error.config?.url || '';
    // Handle blob errors asynchronously
    if (error.response?.data instanceof Blob) {
      return await this.parseBlobError(error, baseUrl);
    }

    // If error response contains standardized API error format
    if (error.response?.data) {
      const data = error.response.data;
      
      // Normalize property names (handle both camelCase and PascalCase)
      const normalizedData = {
        message: data.message || data.Message || '',
        statusCode: data.statusCode || data.StatusCode || data.status || data.Status || error.response.status || 500,
        stack: data.stack || data.Stack || data.stackTrace || data.StackTrace || '',
        url: data.url || data.Url || requestUrl,
        timestamp: data.timestamp || data.Timestamp || new Date().toISOString(),
        correlationId: data.correlationId || data.CorrelationId
      };

      if (normalizedData.message && typeof normalizedData.statusCode === 'number') {
        return {
          message: normalizedData.message,
          stack: normalizedData.stack,
          url: normalizedData.url || requestUrl,
          statusCode: normalizedData.statusCode,
          timestamp: normalizedData.timestamp,
          correlationId: normalizedData.correlationId || this.getCorrelationId(error, data),
          backendSource: 'puppeteer' as BackendSource
        };
      }
    }

    // Handle network errors
    if (error.request && !error.response) {
      return {
        message: error.message || 'Kunde inte ansluta till servern. Kontrollera din internetanslutning.',
        stack: error.stack || '',
        url: requestUrl,
        statusCode: 0,
        timestamp: new Date().toISOString(),
        correlationId: this.getCorrelationId(error),
        backendSource: 'puppeteer' as BackendSource
      };
    }

    // Fallback for other errors
    return {
      message: error.message || 'Ett oväntat fel uppstod',
      stack: error.stack || '',
      url: requestUrl,
      statusCode: error.response?.status,
      timestamp: new Date().toISOString(),
      correlationId: this.getCorrelationId(error),
      backendSource: 'puppeteer' as BackendSource
    };
  }
}
